import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import Select from 'components/shared/select';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import { Button, ButtonGroup, Icon } from 'ui';
import { REACT_QUERY_STATUS } from 'constants/config';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import Notice, { NOTICE_TYPES } from 'components/ui/Notice/Notice';
import Error from 'components/InPersonTraining/shared/Error';
import {
  useGetReadinessSkillsCategories,
  useUpdateReadinessSkillCategory,
} from '../queries/readinessSkills';
import DeleteModal from './DeleteModal';

const EditCategoryModal = ({ closeModal, setBannerProperties }) => {
  const queryClient = useQueryClient();
  const [tempInputTerm, setTempInputTerm] = useState('');
  const [inputTerm, setInputTerm] = useState('');
  const [categoryChosen, setCategoryChosen] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [deleteCategoryChosen, setDeleteCategoryChosen] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState(false);
  const [missingCategoryError, setMissingCategoryError] = useState(false);
  const { data: readinessSkillCategoryData, status: getCategoriesStatus } =
    useGetReadinessSkillsCategories({
      limit: 100,
      offset: 0,
    });
  const { mutate, status } = useUpdateReadinessSkillCategory({
    id: '',
    name: inputTerm,
  });

  const closeDeleteModal = () => {
    setDeleteCategoryChosen(false);
    setDeleteCategory(false);
  };

  const skillsCategories =
    readinessSkillCategoryData?.readinessSkillsCategories
      ?.readinessSkillCategories ?? [];

  const [formData, setFormData] = useState({
    id: '',
    name: '',
  });

  const handleSelectChange = e => {
    const selectedCategoryId = parseInt(e.target.value, 10);
    if (selectedCategoryId === 0) {
      setMissingCategoryError(true);
      setCategoryChosen(false);
    } else {
      const index = skillsCategories.find(
        option => option.id === selectedCategoryId,
      );
      setFormData({ id: index.id, name: index.name });
      setCategoryChosen(true);
      setDeleteCategory(true);
      setTempInputTerm(index.name);
      setMissingCategoryError(false);
    }
  };

  async function handleSubmit() {
    const handleSubmitInput = tempInputTerm.trim();
    setInputTerm(handleSubmitInput);
    try {
      await mutate(
        {
          id: formData.id,
          name: tempInputTerm,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('get-readiness-skills-categories');
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (getCategoriesStatus === REACT_QUERY_STATUS.ERROR) {
      setErrorText(
        t('skills.banner_category_get_error', {
          category_name: formData.name,
        }),
      );
    }

    if (status === REACT_QUERY_STATUS.SUCCESS) {
      closeModal();
      setBannerProperties({
        message: (
          <p
            dangerouslySetInnerHTML={{
              __html: t('skills.banner_category_updated', {
                category_name_old: formData.name,
                category_name_new: tempInputTerm,
              }),
            }}
          />
        ),
        state: 'success',
      });
    }
    if (status === REACT_QUERY_STATUS.ERROR) {
      setErrorText(
        t('skills.banner_edit_error', {
          category_name: formData.name,
        }),
      );
    }
  }, [
    status,
    getCategoriesStatus,
    closeModal,
    setBannerProperties,
    formData.name,
    tempInputTerm,
  ]);

  return (
    <RenderInBody>
      <ModalContainer
        className="ModalTakeover-background Modal-background d-f ai-c"
        onEscape={closeModal}
      >
        <div className="ModalTakeover-container  w-90p">
          <div className="TemplateCreationModal-body">
            <div className="TemplateCreationModal-title">
              {t('skills.edit_category')}
            </div>
            {errorText && (
              <div className="mt-24">
                <Notice
                  iconName="alert"
                  type={NOTICE_TYPES.ERROR}
                  bodyText={errorText}
                />
              </div>
            )}
            <label htmlFor="title" className="TemplateCreationModal-inputTitle">
              {t('skills.category')}
              <span className="c-red ml-1">*</span>
            </label>
            <Select
              name={t('skills.select_category')}
              disabled={skillsCategories.length === 0}
              onChange={handleSelectChange}
              fullWidth
            >
              <option value={0}>{t('skills.select_category')}</option>
              {skillsCategories.map(option => (
                <option key={option?.id} value={option?.id}>
                  {option?.name}
                </option>
              ))}
            </Select>
            {missingCategoryError && !categoryChosen && (
              <div className="SkillsLibrary-createTextErrorContainer">
                <Error message={t('skills.empty_error')} />
              </div>
            )}
            {categoryChosen && (
              <>
                <label
                  htmlFor="title"
                  className="TemplateCreationModal-inputTitle"
                >
                  {t('skills.new_category')}
                  <span className="c-red ml-1">*</span>
                </label>
                <input
                  onChange={e => {
                    setTempInputTerm(e.target.value);
                    setMissingCategoryError(false);
                  }}
                  value={tempInputTerm}
                  className="NewInput NewInput--white NewInput--fw"
                  placeholder={t('skills.add_update_category')}
                  type="text"
                  maxLength="255"
                />
                {missingCategoryError && (
                  <div className="SkillsLibrary-createTextErrorContainer">
                    <Error message={t('skills.empty_error')} />
                  </div>
                )}
              </>
            )}
            <div className="d-f jc-sb">
              <div className="d-f jc-s mt-32">
                {categoryChosen && (
                  <div className="d-f ai-c">
                    <Icon
                      disabled
                      name="delete"
                      className="c-brand-blue mr-2"
                    />
                    <button
                      type="button"
                      className="IconButton"
                      onClick={() => setDeleteCategoryChosen(true)}
                    >
                      <Text variant={TEXT_VARIANTS.BODY_LINK}>
                        {t('skills.delete_category')}
                      </Text>
                    </button>
                  </div>
                )}
              </div>
              <div className="d-f jc-e mt-32">
                <ButtonGroup alignEnd>
                  <Button
                    type="cancel"
                    onClick={closeModal}
                    className="GradebookUpdate-cancelButton"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className="TemplateCreationModal-submitButton"
                    onClick={() =>
                      tempInputTerm.trim().length === 0
                        ? setMissingCategoryError(true)
                        : handleSubmit()
                    }
                  >
                    {t('skills.save_category')}
                  </Button>
                </ButtonGroup>
              </div>
            </div>

            <button
              type="button"
              aria-label={t('common.close')}
              className="u-resetBtn ModalTakeover-closeButton"
              onClick={closeModal}
            >
              <Icon name="error" />
            </button>
          </div>
          <div>
            {deleteCategoryChosen && (
              <DeleteModal
                closeDeleteModal={closeDeleteModal}
                closeModal={closeModal}
                formData={formData}
                setBannerProperties={setBannerProperties}
                setEditModalErrorText={setErrorText}
                deleteCategory={deleteCategory}
              />
            )}
          </div>
        </div>
      </ModalContainer>
    </RenderInBody>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(EditCategoryModal),
);
